import React, { useEffect, useState, useRef } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Space,
  Button,
  Drawer,
  Descriptions,
  Anchor,
  Tag,
  notification,
  Input,
  Form,
  DatePicker,
} from "antd";

import { SearchOutlined } from "@ant-design/icons";
import { Table } from "ant-table-extensions";
import http from "../../../../helpers/http";
import helper from "../../../../helpers/helper";
import config from "../../../../config/config";
import _ from "lodash";
import "./style.css";
import moment from "moment";

function DiwaliBonanza2026MonthlyVerifiedWinners() {
  // ============================================================
  // STATE
  // ============================================================

  const [loading, setloading] = useState(false);
  const [drawerOpen, setdraweropen] = useState(false);

  const [datas, setDatas] = useState([]);
  const [data, setData] = useState({});

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [btnloading, setbtnloading] = useState(false);

  const [form] = Form.useForm();
  const [formError, setFormError] = useState({});

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInput = useRef(null);

  const { Link } = Anchor;

  // ============================================================
  // CONSTANTS
  // ============================================================

  const moduleNamePural = "Dream Big Win Bigger 2026 Monthly Verified Winners";

  const base_url = config.api_url + "admin/leads/diwali-bonanza-2026";

  const azure_object_url =
    config.azure_object_url + "public/uploads/diwali-bonanza-2026/";

  const prizeLabels = {
    SMARTPHONE: "Smartphone",
    GOLD_COIN: "Gold Coin",
    AIRBUDS: "Airbuds",
  };

  // ============================================================
  // VIEW WINNER DETAILS
  // ============================================================

  const handleView = async (id) => {
    try {
      setloading(true);

      const result = await http.get(`${base_url}/monthly-winners/${id}`);

      if (result) {
        setData(result.data);
        setdraweropen(true);
      }
    } catch (err) {
      notification.error({
        message:
          err?.response?.data?.message || "Failed to load winner details.",
      });
    } finally {
      setloading(false);
    }
  };

  // ============================================================
  // CLOSE DRAWER
  // ============================================================

  const handleDrawerClose = () => {
    setData({});
    setdraweropen(false);
  };

  // ============================================================
  // FETCH VERIFIED MONTHLY WINNERS
  // ============================================================

  const fetchDatas = async (month = null) => {
    setloading(true);

    try {
      const params = {
        verified: true,
      };

      if (month) {
        params.month = month;
      }

      const result = await http.get(`${base_url}/monthly-winners`, {
        params,
      });

      if (result && result.data) {
        const winners = result.data.winners || [];

        const verifiedWinners = winners.filter(
          (winner) => winner.is_verified === true,
        );

        const rows = verifiedWinners.map((winner) => ({
          ...winner.entry,
          winnerId: winner._id,
          monthlyWinner: winner,
          prize: winner.prize,
          batch: winner.batch,
          verified_at: winner.verified_at,
          winnerCreatedAt: winner.createdAt,
          createdAt: winner.entry?.createdAt,
        }));

        setDatas(rows);
      } else {
        setDatas([]);
      }
    } catch (err) {
      console.error("Monthly verified winners API failed:", err);

      notification.error({
        message:
          err?.response?.data?.message ||
          "Failed to load monthly verified winners.",
      });

      setDatas([]);
    } finally {
      setloading(false);
    }
  };

  // ============================================================
  // LOAD ALL VERIFIED WINNERS ON PAGE LOAD
  // ============================================================

  useEffect(() => {
    fetchDatas();
  }, []);

  // ============================================================
  // MONTH CHANGE
  // ============================================================

  const handleMonthChange = async (date, dateString) => {
    setSelectedMonth(dateString || null);
    setFormError({});

    await fetchDatas(dateString || null);
  };

  // ============================================================
  // FORM SUBMIT
  // ============================================================

  const handleSubmit = async () => {
    if (!selectedMonth) {
      setFormError({
        month: "Please select a month",
      });

      return;
    }

    setFormError({});
    setbtnloading(true);

    try {
      await fetchDatas(selectedMonth);
    } finally {
      setbtnloading(false);
    }
  };

  // ============================================================
  // SEARCH
  // ============================================================

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();

    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();

    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <div
          style={{
            padding: 8,
          }}
        >
          <Input
            autoFocus
            placeholder="Type text here"
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);

              confirm({
                closeDropdown: false,
              });
            }}
            onPressEnter={() => {
              confirm();
            }}
            onBlur={() => {
              confirm();
            }}
            style={{
              marginBottom: 8,
              display: "block",
            }}
          />

          <Space></Space>
        </div>
      );
    },

    filterIcon: () => {
      return <SearchOutlined />;
    },

    onFilter: (value, record) => {
      return String(record[dataIndex] || "")
        .toLowerCase()
        .includes(value.toLowerCase());
    },
  });

  // ============================================================
  // TABLE COLUMNS
  // ============================================================

  const columns = [
    {
      title: "First Name",
      dataIndex: "first_name",
      key: "first_name",
      ellipsis: true,
    },

    {
      title: "Last Name",
      dataIndex: "last_name",
      key: "last_name",
      ellipsis: true,
    },

    {
      title: "Email Id",
      dataIndex: "email",
      key: "email",
    },

    {
      title: "Contact Number",
      dataIndex: "phone",
      key: "phone",

      render: (text, record) => {
        if (record.country_code && record.phone) {
          return `${record.country_code} ${record.phone}`;
        }

        if (record.phone) {
          return record.phone;
        }

        return "NA";
      },
    },

    {
      title: "Prize",
      dataIndex: "prize",
      key: "prize",

      render: (text, record) => {
        return prizeLabels[record.prize] || record.prize || "NA";
      },
    },

    // {
    //   title: "Verified Date",
    //   dataIndex: "verified_at",
    //   key: "verified_at",

    //   render: (text, record) => {
    //     return record.verified_at
    //       ? helper.UserDate(record.verified_at, "-04:00")
    //       : "NA";
    //   },
    // },

    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",

      render: (text, record) => {
        return record.createdAt
          ? helper.UserDate(record.createdAt, "-04:00")
          : "NA";
      },
    },

    // {
    //   title: "Status",
    //   key: "status",

    //   render: () => {
    //     return <Tag color="green">Verified</Tag>;
    //   },
    // },

    {
      title: "Action",
      key: "action",

      render: (text, record) => {
        return (
          <Space size="middle">
            <Button
              onClick={() => {
                handleView(record.winnerId);
              }}
            >
              View Detail
            </Button>
          </Space>
        );
      },
    },
  ];

  // ============================================================
  // EXPORT FIELDS
  // ============================================================

  const fields: ITableExportFields = {
    first_name: {
      header: "First Name",
      formatter: (_fieldValue, record) => {
        return record.first_name || "NA";
      },
    },
  
    last_name: {
      header: "Last Name",
      formatter: (_fieldValue, record) => {
        return record.last_name || "NA";
      },
    },
  
    phone: {
      header: "Contact Number",
      formatter: (_fieldValue, record) => {
        if (record.country_code && record.phone) {
          return `${record.country_code} ${record.phone}`;
        }
  
        if (record.phone) {
          return record.phone;
        }
  
        return "NA";
      },
    },
  
    email: {
      header: "Email Id",
      formatter: (_fieldValue, record) => {
        return record.email || "NA";
      },
    },
  
    gender: {
      header: "Gender",
      formatter: (_fieldValue, record) => {
        return record.gender || "NA";
      },
    },
  
    date_of_birth: {
      header: "Date of Birth",
      formatter: (_fieldValue, record) => {
        return record.date_of_birth
          ? `="${moment(record.date_of_birth).format("DD-MMM-YYYY")}"`
          : "NA";
      },
    },
  
    address: {
      header: "Address",
      formatter: (_fieldValue, record) => {
        return record.address || "NA";
      },
    },
  
    country: {
      header: "Country",
      formatter: (_fieldValue, record) => {
        return record.country || "NA";
      },
    },
  
    state: {
      header: "State",
      formatter: (_fieldValue, record) => {
        return record.state || "NA";
      },
    },
  
    city: {
      header: "City",
      formatter: (_fieldValue, record) => {
        return record.city
          ? _.replace(_.deburr(record.city), /'/g, "")
          : "NA";
      },
    },
  
    zip: {
      header: "Zip Code",
      formatter: (_fieldValue, record) => {
        return record.zip || "NA";
      },
    },
  
    store: {
      header: "Which store did you buy this Laxmi product from?",
      formatter: (_fieldValue, record) => {
        return record.store || "NA";
      },
    },
  
    campaign_source: {
      header: "How did you get to know about this campaign?",
      formatter: (_fieldValue, record) => {
        return record.campaign_source || "NA";
      },
    },
  
    laxmi_fav_product: {
      header: "What is your favorite product from Laxmi?",
      formatter: (_fieldValue, record) => {
        return record.laxmi_fav_product || "NA";
      },
    },
  
    laxmi_brand_one_word: {
      header: "Can you Describe Laxmi Brand in one word?",
      formatter: (_fieldValue, record) => {
        return record.laxmi_brand_one_word || "NA";
      },
    },
  
    laxmi_ads_desc: {
      header: "Have you seen Laxmi ads before? if yes where?",
      formatter: (_fieldValue, record) => {
        return record.laxmi_ads_desc || "NA";
      },
    },
  
    invoice: {
      header: "Invoice",
      formatter: (_fieldValue, record) => {
        return record.invoice
          ? azure_object_url + record.invoice
          : "NA";
      },
    },
  
    is_invoice_uploaded: {
      header: "Invoice Uploaded",
      formatter: (_fieldValue, record) => {
        if (typeof record.is_invoice_uploaded !== "undefined") {
          return record.is_invoice_uploaded ? "Yes" : "No";
        }
  
        return "NA";
      },
    },
  
    prize: {
      header: "Prize",
      formatter: (_fieldValue, record) => {
        return prizeLabels[record.prize] || record.prize || "NA";
      },
    },

    month: {
        header: "Month",
        formatter: (_fieldValue, record) => {
          return record.batch?.month
            ? moment(record.batch.month, "YYYY-MM").format("MMMM YYYY")
            : "NA";
        },
      },
  
    createdAt: {
      header: "Entry Created At",
      formatter: (_fieldValue, record) => {
        return record.createdAt
          ? helper.UserDate(record.createdAt, "-04:00")
          : "NA";
      },
    },
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div>
      {/* ========================================================
          BREADCRUMB
      ======================================================== */}

      <Breadcrumb
        style={{
          margin: "16px 0",
        }}
      >
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>

        <Breadcrumb.Item>Leads</Breadcrumb.Item>

        <Breadcrumb.Item>Dream Big Win Bigger 2026</Breadcrumb.Item>

        <Breadcrumb.Item>Monthly Winners</Breadcrumb.Item>
      </Breadcrumb>

      {/* ========================================================
          MONTH FILTER
      ======================================================== */}

      <Row gutter={15}>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <Card title="Monthly Winners" bordered={false}>
              <Form layout="inline" form={form} onFinish={handleSubmit}>
                <Form.Item
                  label="Month:"
                  name="month"
                  {...(formError.month && {
                    help: formError.month,
                    validateStatus: "error",
                  })}
                >
                  <DatePicker
                    picker="month"
                    value={
                      selectedMonth ? moment(selectedMonth, "YYYY-MM") : null
                    }
                    format="YYYY-MM"
                    variant="borderless"
                    onChange={handleMonthChange}
                  />
                </Form.Item>

                {/* <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={btnloading}
                      disabled={btnloading || !selectedMonth}
                    >
                      Filter
                    </Button>

                    <Button
                      type="secondary"
                      onClick={() => {
                        setSelectedMonth(null);
                        setDatas([]);
                        setFormError({});
                        form.resetFields();
                      }}
                    >
                      Reset
                    </Button>
                  </Space>
                </Form.Item> */}
              </Form>
            </Card>
          </div>
        </Col>
      </Row>

      {/* ========================================================
          WINNERS TABLE
      ======================================================== */}

      <Row>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <Card bordered={false}>
              <Table
                rowKey={(record) => record.winnerId}
                columns={columns}
                dataSource={datas}
                loading={loading}
                pagination={{
                  defaultPageSize: 10,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showSizeChanger: true,
                }}
                exportableProps={{
                  fields,
                  fileName: moduleNamePural,
                }}
                searchableProps={{
                  fuzzySearch: false,

                  fuseProps: {
                    useExtendedSearch: true,
                  },
                }}
              />
            </Card>

            {/* ==================================================
                WINNER DETAIL DRAWER
            ================================================== */}

            <Drawer
              title="Monthly Winner Details"
              placement="right"
              onClose={handleDrawerClose}
              visible={drawerOpen}
              size="large"
            >
              {data && (
                <Descriptions
                  bordered
                  column={{
                    xxl: 1,
                    xl: 1,
                    lg: 1,
                    md: 1,
                    sm: 1,
                    xs: 1,
                  }}
                >
                  <Descriptions.Item label="First Name">
                    {data.entry?.first_name || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Last Name">
                    {data.entry?.last_name || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Contact Number">
                    {data.entry?.country_code && data.entry?.phone
                      ? `${data.entry.country_code} ${data.entry.phone}`
                      : data.entry?.phone || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Email Id">
                    {data.entry?.email || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Gender">
                    {data.entry?.gender || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Date of Birth">
                  {data.entry?.date_of_birth
                    ? moment(data.entry.date_of_birth).format("D MMMM YYYY")
                    : "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Address">
                    {data.entry?.address || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Country">
                    {data.entry?.country || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="State">
                    {data.entry?.state || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="City">
                    {data.entry?.city
                      ? _.replace(_.deburr(data.entry.city), /'/g, "")
                      : "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Zip Code">
                    {data.entry?.zip || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Which store did you buy this Laxmi product from?">
                    {data.entry?.store || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="How did you get to know about this campaign?">
                    {data.entry?.campaign_source || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="What is your favorite product from Laxmi?">
                    {data.entry?.laxmi_fav_product || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Can you Describe Laxmi Brand in one word?">
                    {data.entry?.laxmi_brand_one_word || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Have you seen Laxmi ads before? if yes where?">
                    {data.entry?.laxmi_ads_desc || "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Invoice">
                    {data.entry?.invoice ? (
                      <a
                        href={azure_object_url + data.entry.invoice}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Invoice
                      </a>
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item label="Invoice Uploaded">
                    {typeof data.entry?.is_invoice_uploaded !== "undefined" ? (
                      data.entry.is_invoice_uploaded ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item label="Prize">
                    {prizeLabels[data.prize] || data.prize || "NA"}
                  </Descriptions.Item>

                  {/* <Descriptions.Item label="Verified">
                    {data.is_verified ? (
                      <Tag color="green">Yes</Tag>
                    ) : (
                      <Tag color="red">No</Tag>
                    )}
                  </Descriptions.Item> */}

                  {/* <Descriptions.Item label="Verified Date">
                    {data.verified_at
                      ? helper.UserDate(data.verified_at, "-04:00")
                      : "NA"}
                  </Descriptions.Item> */}

                  {/* <Descriptions.Item label="Rejected">
                    {data.is_rejected ? (
                      <Tag color="red">Yes</Tag>
                    ) : (
                      <Tag color="green">No</Tag>
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item label="Winner Generated Date">
                    {data.createdAt
                      ? helper.UserDate(data.createdAt, "-04:00")
                      : "NA"}
                  </Descriptions.Item> */}

                  <Descriptions.Item label="Entry Created At">
                    {data.entry?.createdAt
                      ? helper.UserDate(data.entry.createdAt, "-04:00")
                      : "NA"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Month">
                    {data.batch?.month
                      ? moment(data.batch.month, "YYYY-MM").format("MMMM YYYY")
                      : "NA"}
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Drawer>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default DiwaliBonanza2026MonthlyVerifiedWinners;
